import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Title } from '@angular/platform-browser';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { SessionTimeoutComponent } from '@app/shared/components/session-timeout/session-timeout.component';
import { DEFAULT_INTERRUPTSOURCES, Idle } from '@ng-idle/core';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import * as _moment from 'moment';
import { interval, Observable, Subject } from 'rxjs';
import { filter, take, takeUntil, tap } from 'rxjs/operators';
import { Constants } from 'src/app/constants';
import { environment } from 'src/environments/environment';

import { DetectBrowserService } from './core/services/detect-browser.service';
import { LoggingService } from './core/services/logging.service';
import { SpinnerService } from './core/services/spinner.service';
import { SpinnerStateService } from './core/services/spinner-state.service';
import { TokenService } from './core/services/token.service';
import { UnityDirectStateService } from './core/services/unity-direct-state.service';
import { AppNames } from './models/AppNames';
import { Pages } from './models/Pages';
import { SessionTimeoutActions } from './models/session-timeout-actions';
import { checkEmptyHubData } from './shared/utils/hubData';

const moment = _moment;

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html'
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'Unity Express App';
  destroy$: Subject<boolean> = new Subject<boolean>();
  configuration: any;
  isAuthenticated$: Observable<boolean>;
  checkSessionChanged$: Observable<boolean>;
  checkSessionChanged: any;
  browserNameAndVersion: any;
  isIE11: boolean;
  isUnityDirect: boolean = false;
  isDisplay: boolean = false;
  isISAOpen: boolean = false;
  isBillingPageContainer: boolean = false;

  constructor(
    private readonly tokenService: TokenService,
    private readonly idle: Idle,
    private readonly _dialog: MatDialog,
    private readonly _router: Router,
    public oidcSecurityService: OidcSecurityService,
    private readonly ls: LoggingService,
    detectBrowserService: DetectBrowserService,
    private readonly titleService: Title,
    private readonly cdr: ChangeDetectorRef,
    private readonly udStateService: UnityDirectStateService,
    private readonly spinnerService: SpinnerService, //DO NOT REMOVE spinnerService
    private readonly spinnerStateService: SpinnerStateService
  ) {
    this.browserNameAndVersion =
      detectBrowserService.getBrowserNameAndVersion();
    this.validateHubdata();
    this._router.events.pipe().subscribe(e => {
      if (e instanceof NavigationEnd) {
        this.isDisplay = true;
        this.isBillingPageContainer = e.url.includes(Pages.Billing);
        const estimatedRibbonDisallowedPages = [
          Pages.Bundles,
          Pages.Billing,
          Pages.Review,
          Pages.Admin,
          Pages.Error,
          Pages.SessionTimeout
        ];
        this.udStateService.isShowEstimatedMonthlyPayment =
          !estimatedRibbonDisallowedPages.some((page: Pages) =>
            e.url.includes(page)
          ) &&
          ((this.isUnityDirect &&
            !this.udStateService.userAccount.isBundleEnabled) ||
            !this.isUnityDirect);
      } else if (e instanceof NavigationStart) {
        this.isDisplay = false;
      }
      this.cdr.detectChanges();
    });
  }

  reset(): void {
    this.idle.watch();
  }

  ngOnInit(): void {
    this.subscribeToNavState();
    this.subscribeToISAState();

    this.checkForApp();

    this.tokenService.getToken().pipe(takeUntil(this.destroy$)).subscribe();
    this.idle.setIdle(Constants.appConfiguration.idleTimeout); // timeout set to match portal timeout
    this.idle.setTimeout(Constants.appConfiguration.timeout);
    this.idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);
    this.idle.onTimeout.subscribe(() => {
      this._dialog.closeAll();
      this.idle.ngOnDestroy();
      this._router.navigate([Pages.SessionTimeout]);
    });
    this.userIdlingCheck();

    this.ls.sendInfoLevelMessage(
      `Browser: ${this.browserNameAndVersion.name}, Version: ${this.browserNameAndVersion.version} `
    );
    if (
      this.browserNameAndVersion.name === 'IE' &&
      this.browserNameAndVersion.version === '11'
    ) {
      this.isIE11 = true;
    }
  }

  /**
   * Checks for UD/UE and navigates accordingly
   */
  checkForApp(): void {
    const appName = sessionStorage.getItem('appName');
    if (appName === AppNames.UNITY_DIRECT) {
      this.isUnityDirect = true;
      this.udStateService.isAppDirect = true;
      this.titleService.setTitle('Unity Direct');
      if (this.udStateService.canUserAccessNewQuote) {
        this._router.navigate(['/']);
      } else if (this.udStateService.canUserAccessBilling) {
        this.udStateService.openBillingBehaviorSub$.next(true);
      } else {
        this._router.navigate([Pages.Error]);
      }
    } else {
      // Express path
      if (environment.type !== 'beta' && environment.type !== 'production') {
        // For Express Local/Dev/QA, fetch a new token every 4 mins.
        interval(4 * 60 * 1000)
          .pipe(takeUntil(this.destroy$))
          .subscribe(() => {
            this.tokenService.getToken().pipe(take(1)).subscribe();
          });
      }
      this.isUnityDirect = false;
      this.udStateService.isAppDirect = false;
      this.titleService.setTitle('Unity Express App');
      this._router.navigate([Pages.Review]); // Review Resolver will decide if it needs to route to '/unity-express' or to '/review'
    }
  }

  /**
   * Checks for inactivity
   */
  userIdlingCheck(): void {
    this.idle.onIdleStart
      .pipe(filter(() => this._dialog.openDialogs.length === 0))
      .subscribe(() => {
        if (
          !this._router.routerState.snapshot.url.includes(Pages.SessionTimeout)
        ) {
          this._dialog
            .open(SessionTimeoutComponent, {
              data: {},
              height: 'auto',
              width: '700px'
            })
            .afterClosed()
            .subscribe(state => {
              if (state === SessionTimeoutActions.SIGN_OUT) {
                this.idle.ngOnDestroy();
                this._router.navigate([Pages.SessionTimeout]);
                this.ls.sendLogLevelMessage(
                  'Your session has timed out.',
                  this,
                  {
                    error: 'none'
                  }
                );
              } else {
                this.reset();
              }
            });
        }
      });
    if (!this._router.routerState.snapshot.url.includes(Pages.SessionTimeout)) {
      this.idle.watch();
    }
  }

  /**
   * Subsribe to Nav state and hide spinner when necessary
   */
  subscribeToNavState(): void {
    this.udStateService.navInProgressSubject
      .pipe(
        tap((isInProgress: boolean) => {
          if (isInProgress) {
            this.spinnerStateService.removeSpinner();
          }
        }),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  /**
   * Subscribes to open/close state of ISA and changes width accordingly
   */
  subscribeToISAState(): void {
    this.udStateService.isaOpenSubject
      .pipe(takeUntil(this.destroy$))
      .subscribe((isOpen: boolean) => {
        this.isISAOpen = isOpen;
      });
  }

  refreshSession(): void {
    this.oidcSecurityService.forceRefreshSession().subscribe(
      result => {
        console.log(result);
      },
      err => console.log(err)
    );
  }

  refreshSessionCheckSession(): void {
    console.log('start refreshSession');
    this.oidcSecurityService.authorize();
  }

  /**
   * Validates and processes hubData and creates a localHubData to be stored in the browser session
   */
  validateHubdata(): void {
    const localHubData: { key: string; value: string | number } = JSON.parse(
      sessionStorage.getItem('hubData')
    );
    const isEmptyHubData = checkEmptyHubData(localHubData);
    if (!isEmptyHubData) {
      this.populateLocalHubDataVehicleInfo(localHubData);
      this.populateCoBorrowerHubData(localHubData);
      let loanDate, firstPaymentDate, loanDateValid, fpDateValid;
      if (localHubData['LoanDate']) {
        loanDate = moment(localHubData['LoanDate'])?.format('YYYY-MM-DD');
        firstPaymentDate = moment(localHubData['FirstPaymentDate'])?.format(
          'YYYY-MM-DD'
        );
        loanDateValid =
          loanDate && moment(loanDate, 'YYYY-MM-DD', true).isValid();
        fpDateValid =
          firstPaymentDate &&
          moment(firstPaymentDate, 'YYYY-MM-DD', true).isValid();
        sessionStorage.setItem('isLoanDateAutoConfigured', 'false'); //Flag to check if LoanDate/FirstPaymentDate is received from hub post
      } else {
        sessionStorage.setItem('isLoanDateAutoConfigured', 'true'); // Set to true and let the data be populated in the next if block
      }
      if ((!loanDateValid && !fpDateValid) || (!loanDateValid && fpDateValid)) {
        localHubData['LoanDate'] = moment().format('YYYY-MM-DD');
        localHubData['FirstPaymentDate'] = moment()
          .add(1, 'months')
          .format('YYYY-MM-DD');
      } else if (
        (loanDateValid && !fpDateValid) ||
        (loanDateValid &&
          fpDateValid &&
          moment(firstPaymentDate) < moment(loanDate).add(1, 'months'))
      ) {
        localHubData['FirstPaymentDate'] = moment(loanDate)
          .add(1, 'months')
          .format('YYYY-MM-DD');
      }
      sessionStorage.setItem('hubData', JSON.stringify(localHubData));
    }
  }

  /**
   * Populates the localHubData with Vehicle and State info received
   * @param localHubData {{ key: string; value: string | number}}
   */
  populateLocalHubDataVehicleInfo(localHubData: {
    key: string;
    value: string | number;
  }): void {
    localHubData['VIN'] = localHubData['VIN']
      ? localHubData['VIN']?.toUpperCase()
      : null;
    localHubData['State'] = localHubData['State']
      ? localHubData['State']?.toUpperCase()
      : '';
    localHubData['VehicleMake'] = localHubData['VehicleMake']
      ? localHubData['VehicleMake']?.toString()?.toUpperCase()
      : '';
    localHubData['VehicleModel'] = localHubData['VehicleModel']
      ? localHubData['VehicleModel']?.toString()?.toUpperCase()
      : '';
    localHubData['VehicleStyle'] = localHubData['VehicleStyle']
      ? localHubData['VehicleStyle']?.toString()?.toUpperCase()
      : '';
  }

  /**
   * Populate coBorrower info if exists
   * @param localHubData {{
    key: string;
    value: string | number;
  }}
   */
  populateCoBorrowerHubData(localHubData: {
    key: string;
    value: string | number;
  }): void {
    if (
      !localHubData['COBFirstName'] &&
      !localHubData['COBLastName'] &&
      !localHubData['CoBorrowerDOB']
    ) {
      localHubData['COBFirstName'] = '';
      localHubData['COBLastName'] = '';
      localHubData['CoBorrowerDOB'] = '';
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }
}
